import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  static optimisticCountKey = '__AVEROAK_CART_COUNT_HINT__';

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number} */
  get currentCartCount() {
    const rawValue = this.refs.cartBubbleCount.dataset.count ?? this.refs.cartBubbleCount.textContent ?? '0';
    const parsedValue = parseInt(rawValue, 10);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  set currentCartCount(value) {
    const normalizedValue = Number.isFinite(value) ? Math.max(0, value) : 0;

    this.refs.cartBubbleCount.dataset.count = String(normalizedValue);
    this.refs.cartBubbleCount.textContent = normalizedValue < 100 ? String(normalizedValue) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const cartItemCount = event.detail.resource?.item_count;
    const itemCount = event.detail.data?.itemCount ?? 0;
    const source = event.detail.data?.source;
    const comingFromProductForm = source === 'product-form-component';
    const optimisticHint = this.getOptimisticHint();
    const nextItemCount =
      typeof cartItemCount === 'number'
        ? cartItemCount
        : comingFromProductForm
          ? optimisticHint ?? this.currentCartCount + itemCount
          : itemCount;

    this.renderCartBubble(nextItemCount);

    if (typeof cartItemCount === 'number' || comingFromProductForm || source === 'ch-cart-drawer-sync') {
      this.clearOptimisticHint();
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const normalizedItemCount = Number.isFinite(itemCount) ? Math.max(0, itemCount) : 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', normalizedItemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', normalizedItemCount === 0);

    this.currentCartCount = normalizedItemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', normalizedItemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = undefined;
    }

    this.refs.cartBubble.classList.remove('cart-bubble--animating');

    if (!animate || normalizedItemCount === 0) return;

    // Force restart of the quick cart count animation so count changes feel immediate.
    void this.refs.cartBubble.offsetWidth;
    this.refs.cartBubble.classList.add('cart-bubble--animating');

    this.#animationTimeout = window.setTimeout(() => {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
      this.#animationTimeout = undefined;
    }, 220);
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  getOptimisticHint() {
    try {
      const rawHint = window[CartIcon.optimisticCountKey];
      if (!rawHint || typeof rawHint.count !== 'number' || typeof rawHint.timestamp !== 'number') return null;
      if (rawHint.source !== 'product-form-component') return null;
      if (Date.now() - rawHint.timestamp > 2500) return null;

      return Math.max(0, rawHint.count);
    } catch (_) {
      return null;
    }
  }

  clearOptimisticHint() {
    try {
      delete window[CartIcon.optimisticCountKey];
    } catch (_) {
      // no-op
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
